import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuth } from '@/lib/auth';

const routes: RouteRecordRaw[] = [
  { path: '/',                name: 'home',           component: () => import('@/pages/Home.vue') },

  // Products / catalog
  { path: '/products',        name: 'products',       component: () => import('@/pages/ProductList.vue') },
  { path: '/products/:slug',  name: 'product-detail', component: () => import('@/pages/ProductDetail.vue') },
  { path: '/brands',          name: 'brands',         component: () => import('@/pages/BrandList.vue') },
  { path: '/brands/:slug',    name: 'brand-detail',   component: () => import('@/pages/ProductList.vue') },

  // Hosting (v1 parity)
  { path: '/hosting',                          name: 'hosting',              component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Hosting' } },
  { path: '/hosting/virtual-private-servers',  name: 'hosting-vps',          component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Virtual Private Servers' } },
  { path: '/hosting/dedicated-servers',        name: 'hosting-dedicated',    component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Dedicated Servers' } },
  { path: '/hosting/stock-clearance',          name: 'hosting-clearance',    component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Stock Clearance' } },
  { path: '/solutions',       name: 'solutions',      component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Solutions' } },
  { path: '/blog',            name: 'blog',           component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Blog' } },
  { path: '/partners',        name: 'partners',       component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Partners' } },
  { path: '/about',           name: 'about',          component: () => import('@/pages/Placeholder.vue'), meta: { title: 'About' } },
  { path: '/contact',         name: 'contact',        component: () => import('@/pages/Placeholder.vue'), meta: { title: 'Contact' } },

  // Auth
  { path: '/login',           name: 'login',          component: () => import('@/pages/Login.vue') },
  { path: '/verify-otp',      name: 'verify-otp',     component: () => import('@/pages/VerifyOtp.vue') },
  { path: '/account',         name: 'account',        component: () => import('@/pages/Account.vue'), meta: { requiresAuth: true, title: 'Account' } },

  // Admin
  { path: '/admin',                name: 'admin',            component: () => import('@/pages/admin/AdminDashboard.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Admin' } },
  { path: '/admin/hero-slides',    name: 'admin-hero',       component: () => import('@/pages/admin/HeroSlidesAdmin.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Hero slides' } },
  { path: '/admin/brands',         name: 'admin-brands',     component: () => import('@/pages/admin/BrandsAdmin.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Brands' } },
  { path: '/admin/categories',     name: 'admin-categories', component: () => import('@/pages/admin/CategoriesAdmin.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Categories' } },
  { path: '/admin/products',       name: 'admin-products',   component: () => import('@/pages/admin/ProductsAdmin.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Products' } },
  { path: '/admin/products/:id',   name: 'admin-product-edit', component: () => import('@/pages/admin/ProductEdit.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Edit product' } },
  { path: '/admin/menus',          name: 'admin-menus',      component: () => import('@/pages/admin/MenusAdmin.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Header menus' } },
  { path: '/admin/footer',         name: 'admin-footer',     component: () => import('@/pages/admin/FooterAdmin.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Footer' } },
  { path: '/admin/settings',       name: 'admin-settings',   component: () => import('@/pages/admin/SettingsAdmin.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'CMS settings' } },
  { path: '/admin/import',         name: 'admin-import',     component: () => import('@/pages/admin/ImportFromV1.vue'), meta: { requiresAuth: true, requiresAdmin: true, title: 'Import' } },

  { path: '/:catchAll(.*)',   name: 'not-found',      component: () => import('@/pages/NotFound.vue') },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    if (saved) return saved;
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  if (!to.meta?.requiresAuth) return true;
  const { user, loading, refresh, isAdmin } = useAuth();
  if (loading.value) await refresh();
  if (!user.value) return { name: 'login', query: { redirect: to.fullPath } };
  if (to.meta?.requiresAdmin && !isAdmin.value) return { name: 'account' };
  return true;
});

router.afterEach((to) => {
  const base = 'Triple Network';
  const t = (to.meta?.title as string) || (to.name as string);
  document.title = t && t !== 'home' ? `${t} · ${base}` : `${base} — Wireless, Network & Connectivity`;
});

export default router;
